import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

export type UsePressableTriggerRefProps = {
	onOpen: () => void;
	onClose?: () => void;
	isOpen?: boolean;
	isDisabled?: boolean;
	openedByRef?: MutableRefObject<'hover' | 'click' | null>;
};

export function usePressableTriggerRef({
	onOpen,
	onClose,
	isOpen = false,
	isDisabled = false,
	openedByRef,
}: UsePressableTriggerRefProps): (element: HTMLElement | null) => void {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const handleOpen = useCallback(() => {
		if (isOpen) {
			onClose?.();
		} else {
			if (openedByRef) {
				openedByRef.current = 'click';
			}
			onOpen();
		}
	}, [isOpen, onClose, openedByRef, onOpen]);

	const keydownListener = useCallback(
		(event: Event) => {
			if (event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ')) {
				event.preventDefault();
				handleOpen();
			}
		},
		[handleOpen],
	);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cleanUpEventListeners();
				}
				if (element) {
					if (!isDisabled) {
						addEventListener(element, 'click', handleOpen);
					}
					// Always attach keydown for keyboard accessibility
					addEventListener(element, 'keydown', keydownListener);
				}
				nodeRef.current = element;
			}
		},
		[handleOpen, keydownListener, addEventListener, cleanUpEventListeners, isDisabled],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return refCallback;
}
